/**
 * AGC: Interactive P2P Battle Component - REDESIGNED TO MATCH QUICKBATTLE
 * AGC: Real-time turn-based battle interface for Player vs Player
 * AGC: UI matches PvE (QuickBattle) exactly for consistency
 * AGC: Uses reusable components: BattleTop, BattleCenter, BattleLog
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Socket } from 'socket.io-client'
import { ethers } from 'ethers'
import { usePlayerProgression } from '../hooks/usePlayerProgression'
import { playSoundSafe, unlockAudioPlayback } from '../utils/audio'
import { BattleTurnModal, BattleTurnSnapshot } from './modals/BattleTurnModal'
import { BattleTop, PlayerStats as BattleTopPlayerStats, OpponentInfo } from './battle/BattleTop'
import { BattleCenter, Choice, Element } from './battle/BattleCenter'
import { BattleLog, TurnResult as BattleLogTurnResult } from './battle/BattleLog'

interface InteractiveP2PBattleProps {
  battleId: string
  playerAddress: string
  opponentAddress: string
  opponentLevel: number
  socket: Socket | null
  selectedBuffs: number[] // AGC: NFT buff IDs that player escrowed for this battle
  selectedPotion?: number | null // AGC: Potion token ID that player escrowed for this battle
  onBattleEnd: (won: boolean, turns: number, isDraw?: boolean, resolutionTxHash?: string | null) => void
  onBackToMenu: () => void
}

// AGC: Choice and Element types are now imported from BattleCenter

type BuffStatus = 'activated' | 'failed' | 'disabled' | 'available' | string

interface BuffStatusEntry {
  id: number
  name: string
  status: BuffStatus
}

interface DamageBreakdown {
  baseName: string
  baseDamage: number
  levelBonus: number
  elementBonus: number
  elementName: string
  rarityBonus: number
  rarityName: string
  hasteBonus: number
  rawDamage: number
  defense: number
  afterDefense: number
  pierceReduction?: number
  shieldReduction?: number
  frostReduction?: number
}

interface TurnResult {
  turnNumber: number
  yourDamage: number
  opponentDamage: number
  yourEnergy: number
  opponentEnergy: number
  yourChoice: Choice
  opponentChoice: Choice
  yourElement: Element
  opponentElement: Element
  yourBuffs?: number[] // AGC: Buff IDs used by player
  opponentBuffs?: number[] // AGC: Buff IDs used by opponent
  yourActivatedBuff?: string | null // AGC: Which buff proc'd (SHIELD, PIERCE, etc.)
  opponentActivatedBuff?: string | null // AGC: Opponent's proc'd buff
  yourFrostUsed?: boolean // AGC: Track if Frost was used by player
  opponentFrostUsed?: boolean // AGC: Track if Frost was used by opponent
  yourBreakdown?: DamageBreakdown // AGC: Damage calculation details
  opponentBreakdown?: DamageBreakdown // AGC: Opponent damage details
  yourBuffStatus?: BuffStatusEntry[]
  opponentBuffStatus?: BuffStatusEntry[]
  log?: any
}

export const InteractiveP2PBattle: React.FC<InteractiveP2PBattleProps> = ({
  battleId,
  playerAddress,
  opponentAddress,
  opponentLevel,
  socket,
  selectedBuffs, // AGC: CRITICAL - Passed directly as prop, no stale state issue
  selectedPotion: propSelectedPotion = null, // AGC: Potion token ID escrowed for this battle
  onBattleEnd,
  onBackToMenu
}) => {
  // AGC: Get player stats for XP/Level display
  const { playerStats } = usePlayerProgression(playerAddress)

  // AGC: Battle state - check for reconnect state from sessionStorage on mount
  const getInitialState = () => {
    try {
      const reconnectState = sessionStorage.getItem('p2pReconnectState')
      if (reconnectState) {
        const state = JSON.parse(reconnectState)
        // AGC: Clear after reading so we don't use stale data next time
        sessionStorage.removeItem('p2pReconnectState')
        console.log(`🔄 [InteractiveP2P] Loaded reconnect state: Turn ${state.currentTurn}, ${state.turnTimeLeft}s left`)
        return state
      }
    } catch (error) {
      console.warn('🔄 [InteractiveP2P] Failed to load reconnect state:', error)
    }
    return null
  }
  
  const initialState = getInitialState()
  const [currentTurn, setCurrentTurn] = useState(initialState?.currentTurn || 1)
  const [yourEnergy, setYourEnergy] = useState(initialState?.yourEnergy || 100)
  const [opponentEnergy, setOpponentEnergy] = useState(initialState?.opponentEnergy || 100)
  const [turnTimeLeft, setTurnTimeLeft] = useState(initialState?.turnTimeLeft || 60)

  // AGC: Turn input state
  const origamiOptions = useMemo(() => ['rock', 'paper', 'scissors'] as Choice[], [])
  const elementOptions = useMemo(() => ['water', 'fire', 'ice', 'wind'] as Element[], [])
  const defaultChoiceIndex = 1
  const defaultElementIndex = 1

  const [selectedChoice, setSelectedChoice] = useState<Choice>(origamiOptions[defaultChoiceIndex])
  const [selectedElement, setSelectedElement] = useState<Element>(elementOptions[defaultElementIndex])
  const [_, setChoiceCarouselIndex] = useState(defaultChoiceIndex)
  const [__, setElementCarouselIndex] = useState(defaultElementIndex)
  const [turnSubmitted, setTurnSubmitted] = useState(initialState?.turnSubmitted || false)
  
  // AGC: Potion selection state - use prop if provided, otherwise use internal state
  const [potionUsed, setPotionUsed] = useState(false)
  const [equippedPotion, setEquippedPotion] = useState<{ tokenId: number; potionType: number; rarity: number; magnitude: number } | null>(null)
  const [selectedPotionForTurn, setSelectedPotionForTurn] = useState<number | null>(null)
  
  // AGC: Load potion metadata ONCE from propSelectedPotion (the potion player BROUGHT to battle)
  // AGC: This should NOT change when user toggles selection during battle - equippedPotion stays constant
  useEffect(() => {
    const loadPotionMetadata = async () => {
      // AGC: Only load once from propSelectedPotion - this is the escrowed potion
      if (propSelectedPotion === null) {
        setEquippedPotion(null)
        return // AGC: No potion brought to battle - keep equippedPotion as null
      }
      
      // AGC: Already loaded - don't reload
      if (equippedPotion !== null && equippedPotion.tokenId === propSelectedPotion) {
        return
      }
      
      try {
        const { CONTRACT_ADDRESSES, CLASSIC_POTION_1155_ABI } = await import('../hooks/useContract')
        const { ethers } = await import('ethers')
        const rpcUrl = import.meta.env.VITE_BASE_RPC_URL || 'https://sepolia.base.org'
        const provider = new ethers.JsonRpcProvider(rpcUrl)
        
        if (CONTRACT_ADDRESSES.CLASSIC_POTION_1155 === '0x0000000000000000000000000000000000000000') {
          console.warn('⚠️ Classic Potion contract not configured')
          return
        }
        
        const contract = new ethers.Contract(
          CONTRACT_ADDRESSES.CLASSIC_POTION_1155,
          CLASSIC_POTION_1155_ABI,
          provider
        )
        
        const metadata = await contract.getPotionMetadata(propSelectedPotion)
        setEquippedPotion({
          tokenId: propSelectedPotion,
          potionType: Number(metadata[0]),
          rarity: Number(metadata[1]),
          magnitude: Number(metadata[2])
        })
        console.log('✅ [InteractiveP2P] Loaded potion metadata for BattleCenter:', { potionType: metadata[0], rarity: metadata[1], magnitude: metadata[2] })
      } catch (error) {
        console.warn('⚠️ [InteractiveP2P] Failed to load potion metadata:', error)
        setEquippedPotion(null)
      }
    }
    
    loadPotionMetadata()
  }, [propSelectedPotion, equippedPotion])

  const submitButtonRef = useRef<HTMLButtonElement | null>(null)
  const [highlightSubmitButton, setHighlightSubmitButton] = useState(false)

  // AGC: Battle log - use reconnect state if available
  const [battleLog, setBattleLog] = useState<TurnResult[]>(initialState?.battleLog || [])
  const [waitingForOpponent, setWaitingForOpponent] = useState(initialState?.turnSubmitted || false)
  const [turnSnapshot, setTurnSnapshot] = useState<BattleTurnSnapshot | null>(null)

  // AGC: Battle end state
  const [battleEnded, setBattleEnded] = useState(false)
  const [winner, setWinner] = useState<string | null>(null)
  const [isResolvingOnChain, setIsResolvingOnChain] = useState(false)
  const [showProcessingOverlay, setShowProcessingOverlay] = useState(false)

  // AGC: CRITICAL - selectedBuffs is passed directly as prop, no stale state issue
  const submitTurnRef = useRef<((choice: Choice, elem: Element) => void) | undefined>(undefined)
  
  const submitTurn = useCallback((choice: Choice, elem: Element) => {
    if (turnSubmitted) return

    if (!socket) {
      console.warn('AGC: Ignoring submitTurn - socket not connected')
      return
    }

    console.log(' Submitting turn:', { choice, elem, buffs: selectedBuffs, potion: selectedPotionForTurn })

    unlockAudioPlayback()

    // AGC: Mark potion as used after submission if it was selected for this turn
    if (selectedPotionForTurn !== null && equippedPotion && selectedPotionForTurn === equippedPotion.tokenId) {
      setPotionUsed(true)
    }

    socket.emit('submit_turn', {
      battleId,
      playerAddress,
      turn: { 
        choice, 
        element: elem, 
        buffs: selectedBuffs, // AGC: Use selectedBuffs directly from props
        potions: selectedPotionForTurn !== null ? [selectedPotionForTurn] : [] // AGC: Include potion token ID if selected for this turn
      }
    })

    setTurnSubmitted(true)
    setWaitingForOpponent(true)
  }, [socket, battleId, playerAddress, selectedBuffs, selectedPotionForTurn, equippedPotion, turnSubmitted])

  submitTurnRef.current = submitTurn

  const handleAutoSubmit = useCallback(() => {
    const randomChoiceIndex = Math.floor(Math.random() * origamiOptions.length)
    const randomElementIndex = Math.floor(Math.random() * elementOptions.length)
    const randomChoice = origamiOptions[randomChoiceIndex]
    const randomElement = elementOptions[randomElementIndex]

    setChoiceCarouselIndex(randomChoiceIndex)
    setElementCarouselIndex(randomElementIndex)
    setSelectedChoice(randomChoice)
    setSelectedElement(randomElement)

    if (submitTurnRef.current) {
      submitTurnRef.current(randomChoice, randomElement)
    }
  }, [origamiOptions, elementOptions])

  // AGC: Turn timer countdown
  useEffect(() => {
    if (turnSubmitted || battleEnded) return

    const timer = setInterval(() => {
      setTurnTimeLeft((prev) => {
        const newTime = prev <= 1 ? 60 : prev - 1
        
        // AGC: Update sessionStorage every 5 seconds for TopNav indicator
        if (newTime % 5 === 0 || newTime <= 10) {
          try {
            const savedBattle = sessionStorage.getItem('p2pActiveBattle')
            if (savedBattle) {
              const battleData = JSON.parse(savedBattle)
              battleData.turnTimeLeft = newTime
              battleData.currentTurn = currentTurn
              sessionStorage.setItem('p2pActiveBattle', JSON.stringify(battleData))
            }
          } catch (error) {
            // AGC: Ignore errors silently
          }
        }
        
        if (prev <= 1) {
          if (!turnSubmitted) {
            handleAutoSubmit()
          }
          return 60
        }
        return newTime
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [turnSubmitted, battleEnded, handleAutoSubmit, currentTurn])

  useEffect(() => {
    setShowProcessingOverlay(false)
  }, [battleId])

  // AGC: Helper - scroll to and highlight the submit button when the player needs to act
  const focusSubmitButton = useCallback(() => {
    if (!submitButtonRef.current) {
      return
    }
    setHighlightSubmitButton(true)
  }, [])

  // AGC: Automatically clear the button highlight after a short delay
  useEffect(() => {
    if (!highlightSubmitButton) return
    const timeoutId = setTimeout(() => setHighlightSubmitButton(false), 3000)
    return () => clearTimeout(timeoutId)
  }, [highlightSubmitButton])

  // AGC: Helper functions - defined before socket useEffect
  const getChoiceImage = (choice: Choice | string) => {
    const normalizedChoice = choice?.toLowerCase()
    switch (normalizedChoice) {
      case 'rock': return '/rock.png'
      case 'paper': return '/paper.png'
      case 'scissors': return '/scissor.png'
      default: return '/rock.png'
    }
  }

  const getElementEmoji = (elem: Element | string) => {
    const normalizedElem = elem?.toLowerCase()
    switch (normalizedElem) {
      case 'water': return '💧'
      case 'fire': return '🔥'
      case 'ice': return '❄️'
      case 'wind': return '💨'
      default: return ''
    }
  }

  // AGC: Using PNG images instead of emojis
  const buffMeta: Record<number, { icon: string; name: string }> = useMemo(
    () => ({
      0: { icon: '/shield.png', name: 'SHIELD' },
      1: { icon: '/pierce.png', name: 'PIERCE' },
      2: { icon: '/haste.png', name: 'HASTE' },
      3: { icon: '/frost.png', name: 'FROST' }
    }),
    []
  )

  const mapBuffStatusEntries = useCallback(
    (
      statusEntries?: BuffStatusEntry[],
      fallbackIds?: number[],
      activatedBuffName?: string | null,
      frostUsed?: boolean
    ): BuffStatusEntry[] => {
      if (statusEntries && statusEntries.length > 0) {
        return statusEntries.map((entry) => {
          const id = typeof entry.id === 'number' ? entry.id : fallbackIds?.find((buffId) => buffMeta[buffId]?.name === entry.name)
          return {
            id: id ?? -1,
            name: entry.name,
            status: entry.status
          }
        })
      }

      if (!fallbackIds || fallbackIds.length === 0) {
        return []
      }

      const normalizedActivated = activatedBuffName ? activatedBuffName.toString().toUpperCase() : null

      return fallbackIds.map((buffId) => {
        const meta = buffMeta[buffId]
        const name = meta?.name || `BUFF_${buffId}`

        let status: BuffStatus = 'failed'
        if (normalizedActivated === name) {
          status = 'activated'
        } else if (name === 'FROST' && frostUsed) {
          status = 'disabled'
        }

        return {
          id: buffId,
          name,
          status
        }
      })
    },
    [buffMeta]
  )

  const toP2PTurnSnapshot = useCallback(
    (turn: TurnResult): BattleTurnSnapshot => {
      const yourEntries = mapBuffStatusEntries(turn.yourBuffStatus, turn.yourBuffs, turn.yourActivatedBuff, turn.yourFrostUsed)
      const opponentEntries = mapBuffStatusEntries(
        turn.opponentBuffStatus,
        turn.opponentBuffs,
        turn.opponentActivatedBuff,
        turn.opponentFrostUsed
      )

      const chipify = (entries: BuffStatusEntry[]) =>
        entries.map((entry) => {
          const meta = entry.id >= 0 ? buffMeta[entry.id] : undefined
          const baseLabel = entry.name || meta?.name || (entry.id >= 0 ? `BUFF_${entry.id}` : 'BUFF')
          const statusValue = entry.status?.toLowerCase()
          let status: 'active' | 'ready' | 'used' = 'ready'
          if (statusValue === 'activated') {
            status = 'active'
          } else if (statusValue === 'disabled') {
            status = 'used'
          }
          return { label: baseLabel, status }
        })

      return {
        turnNumber: turn.turnNumber,
        yourChoice: turn.yourChoice,
        opponentChoice: turn.opponentChoice,
        yourElement: turn.yourElement,
        opponentElement: turn.opponentElement,
        yourDamage: turn.yourDamage,
        opponentDamage: turn.opponentDamage,
        yourEnergy: turn.yourEnergy,
        opponentEnergy: turn.opponentEnergy,
        yourBuffs: chipify(yourEntries),
        opponentBuffs: chipify(opponentEntries),
        yourBreakdown: turn.yourBreakdown,
        opponentBreakdown: turn.opponentBreakdown,
      }
    },
    [buffMeta, mapBuffStatusEntries]
  )

  const handleBattleResolution = useCallback(async (winnerAddress: string) => {
    if (isResolvingOnChain) return

    setIsResolvingOnChain(true)

    try {
      if (!window.ethereum) throw new Error('No wallet detected')

      const provider = new ethers.BrowserProvider(window.ethereum)
      const signer = await provider.getSigner()

      const p2pManagerAddress = import.meta.env.VITE_P2P_MANAGER_ADDRESS
      const buff1155Address = import.meta.env.VITE_BUFF1155_ADDRESS
      const potion1155Address = import.meta.env.VITE_CLASSIC_POTION1155_ADDRESS || '0x0000000000000000000000000000000000000000'

      // AGC: V2 contract requires 5 parameters (buff1155 + potion1155)
      const p2pManager = new ethers.Contract(
        p2pManagerAddress,
        ['function resolveBattle(bytes32 battleId, address winner, address loser, address buff1155Address, address potion1155Address) external'],
        signer
      )

      const loserAddress = winnerAddress.toLowerCase() === playerAddress.toLowerCase()
        ? opponentAddress
        : playerAddress

      console.log(' Calling resolveBattle on-chain...')
      console.log(`  Battle ID: ${battleId}`)
      console.log(`  Winner: ${winnerAddress}`)
      console.log(`  Loser: ${loserAddress}`)
      console.log(`  Buff1155: ${buff1155Address}`)
      console.log(`  Potion1155: ${potion1155Address}`)
      console.log(`  Final Turn: ${currentTurn}`)

      const tx = await p2pManager.resolveBattle(battleId, winnerAddress, loserAddress, buff1155Address, potion1155Address)
      console.log(`[WAIT] Transaction sent: ${tx.hash}`)
      await tx.wait()
      console.log(`[OK] Battle resolved on-chain!`)

    } catch (error: any) {
      console.error(`[ERROR] Resolution error:`, error)

      if (error.message?.includes('already resolved') ||
          error.data?.message?.includes('already resolved') ||
          error.reason?.includes('already resolved')) {
        console.log(`[OK] Battle already resolved by other player`)
        return
      }

      console.log(`[WARN] Resolution attempt failed, but battle likely already resolved`)
    } finally {
      setIsResolvingOnChain(false)
    }
  }, [isResolvingOnChain, battleId, playerAddress, opponentAddress, currentTurn])

  // AGC: Listen for WebSocket events
  useEffect(() => {
    if (!socket) {
      console.warn('AGC: Socket not ready, skipping listener registration')
      return
    }

    socket.on('turn_result', (data: TurnResult) => {
      console.log(' Turn result received:', data)

      setBattleLog((prev) => [...prev, data])
      setYourEnergy(data.yourEnergy)
      setOpponentEnergy(data.opponentEnergy)
      setWaitingForOpponent(false)
      setTurnSnapshot(toP2PTurnSnapshot(data))

      // AGC: Play punch sound when turn ends
      playSoundSafe('/sounds/punch.mp3', 0.5)

      if (data.yourEnergy > 0 && data.opponentEnergy > 0) {
        setTurnSubmitted(false)
        setSelectedChoice(origamiOptions[defaultChoiceIndex])
        setSelectedElement(elementOptions[defaultElementIndex])
        setChoiceCarouselIndex(defaultChoiceIndex)
        setElementCarouselIndex(defaultElementIndex)
        setTurnTimeLeft(60)
        setSelectedPotionForTurn(null) // AGC: Reset potion selection for next turn (potions are one-time use per turn)
      } else {
        setShowProcessingOverlay(true)
      }
    })

    socket.on('next_turn', (data) => {
      setCurrentTurn(data.turnNumber)
      
      // AGC: Update in-battle sessionStorage with new turn number
      try {
        const savedBattle = sessionStorage.getItem('p2pActiveBattle')
        if (savedBattle) {
          const battleData = JSON.parse(savedBattle)
          battleData.currentTurn = data.turnNumber
          battleData.turnTimeLeft = 60
          sessionStorage.setItem('p2pActiveBattle', JSON.stringify(battleData))
        }
      } catch (error) {
        console.warn('🔄 Failed to update turn in sessionStorage:', error)
      }
    })

    socket.on('battle_end', (data) => {
      console.log(' Battle ended:', data)
      setBattleEnded(true)
      setWinner(data.winner)
      setTurnTimeLeft(0)
      setTurnSnapshot(null)
      setShowProcessingOverlay(false)
      
      // AGC: Clear in-battle sessionStorage when battle ends
      try {
        sessionStorage.removeItem('p2pActiveBattle')
        console.log('🔄 [InteractiveP2P] Cleared in-battle state from sessionStorage')
      } catch (error) {
        console.warn('🔄 Failed to clear in-battle sessionStorage:', error)
      }

      // AGC: Check if it's a draw
      if (data.isDraw) {
        console.log(' Battle ended in a DRAW!')
        // AGC: Play draw sound
        playSoundSafe('/sounds/punch.mp3', 0.4)
        setShowProcessingOverlay(false)
        setIsResolvingOnChain(false)
        onBattleEnd(false, data.battleLog?.length || 1, true, data.resolutionTxHash)
        return
      }

      // AGC: Not a draw - show popup IMMEDIATELY
      const isPlayerWinner = data.winner.toLowerCase() === playerAddress.toLowerCase()

      // AGC: Play winner/loser sound
      if (isPlayerWinner) {
        playSoundSafe('/sounds/youwin.mp3', 0.6)
      } else {
        playSoundSafe('/sounds/gameover.mp3', 0.6)
      }

      setShowProcessingOverlay(false)
      setIsResolvingOnChain(isPlayerWinner)
      onBattleEnd(isPlayerWinner, data.turns || 1, false, data.resolutionTxHash)

      // AGC: Only the winner resolves on-chain to avoid unauthorized errors
      if (isPlayerWinner) {
        handleBattleResolution(data.winner)
      }
    })

    return () => {
      socket.off('turn_result')
      socket.off('next_turn')
      socket.off('battle_end')
    }
  }, [socket, battleLog.length, currentTurn, onBattleEnd, playerAddress, toP2PTurnSnapshot, handleBattleResolution, origamiOptions, elementOptions, defaultChoiceIndex])

  // AGC: P2P RECONNECTION SYSTEM - Check for battle state on mount
  // AGC: Only check if we didn't just receive battle_start (new battle vs reconnect)
  const hasCheckedBattleReconnectRef = useRef(false)
  const battleStartTimeRef = useRef<number | null>(null)
  const componentMountTimeRef = useRef<number>(Date.now())
  
  // AGC: Track when battle_start is received (new battle, not reconnect)
  useEffect(() => {
    if (!socket) return
    
    const handleBattleStart = (data: any) => {
      if (data.battleId === battleId) {
        console.log('🔄 [InteractiveP2P] battle_start received - marking as new battle')
        battleStartTimeRef.current = Date.now()
      }
    }
    
    socket.on('battle_start', handleBattleStart)
    return () => {
      socket.off('battle_start', handleBattleStart)
    }
  }, [socket, battleId])
  
  useEffect(() => {
    if (!socket || !battleId || !playerAddress || hasCheckedBattleReconnectRef.current) return
    
    // AGC: Don't check for reconnection if component just mounted (likely a new battle)
    // AGC: If component mounted less than 3 seconds ago, skip reconnection check
    const timeSinceMount = Date.now() - componentMountTimeRef.current
    if (timeSinceMount < 3000) {
      console.log('🔄 [InteractiveP2P] Skipping reconnection check - component just mounted (', Math.round(timeSinceMount / 1000), 's ago) - likely new battle')
      hasCheckedBattleReconnectRef.current = true
      return
    }
    
    // AGC: Don't check for reconnection if we just received battle_start (it's a new battle)
    // AGC: If battle_start was received less than 5 seconds ago, it's a new battle
    const timeSinceBattleStart = battleStartTimeRef.current 
      ? Date.now() - battleStartTimeRef.current 
      : Infinity
    
    if (timeSinceBattleStart < 5000) {
      console.log('🔄 [InteractiveP2P] Skipping reconnection check - new battle just started (', Math.round(timeSinceBattleStart / 1000), 's ago)')
      hasCheckedBattleReconnectRef.current = true
      return
    }
    
    // AGC: Delay reconnection check by 1 second to allow battle state to initialize
    const timer = setTimeout(() => {
      console.log('🔄 [InteractiveP2P] Checking for battle reconnection (battle may be existing)...')
      hasCheckedBattleReconnectRef.current = true
      socket.emit('check_battle_reconnect', { battleId, playerAddress })
    }, 1000)
    
    return () => clearTimeout(timer)
  }, [socket, battleId, playerAddress])
  
  // AGC: P2P RECONNECTION SYSTEM - Visibility change handler for mid-battle reconnection
  const lastVisibilityCheckRef = useRef<number>(0)
  
  useEffect(() => {
    if (!socket || !battleId || !playerAddress || battleEnded) return
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !battleEnded) {
        // AGC: Debounce - don't check more than once per 2 seconds
        const now = Date.now()
        if (now - lastVisibilityCheckRef.current < 2000) {
          console.log('🔄 [InteractiveP2P] Visibility check debounced')
          return
        }
        lastVisibilityCheckRef.current = now
        
        // AGC: Only check if component has been mounted for at least 5 seconds (not a new battle)
        const timeSinceMount = now - componentMountTimeRef.current
        if (timeSinceMount < 5000) {
          console.log('🔄 [InteractiveP2P] Skipping visibility check - new battle (', Math.round(timeSinceMount / 1000), 's)')
          return
        }
        
        console.log('🔄 [InteractiveP2P] Page visible - checking battle reconnect')
        socket.emit('check_battle_reconnect', { battleId, playerAddress })
      }
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [socket, battleId, playerAddress, battleEnded])
  
  // AGC: P2P RECONNECTION SYSTEM - Handle battle state sync from backend
  useEffect(() => {
    if (!socket) return
    
    const handleBattleStateSync = (data: any) => {
      console.log('🔄 [InteractiveP2P] battle_state_sync received:', data)
      
      if (data.battleId !== battleId) {
        console.log('🔄 [InteractiveP2P] Ignoring state sync for different battle')
        return
      }
      
      // AGC: Restore battle state
      setCurrentTurn(data.currentTurn)
      setYourEnergy(data.yourEnergy)
      setOpponentEnergy(data.opponentEnergy)
      setTurnTimeLeft(data.turnTimeLeft)
      setTurnSubmitted(data.turnSubmitted)
      
      // AGC: Restore battle log
      if (data.battleLog && Array.isArray(data.battleLog)) {
        setBattleLog(data.battleLog)
      }
      
      // AGC: Reset waiting state based on turn submission
      if (data.turnSubmitted) {
        setWaitingForOpponent(true)
      } else {
        setWaitingForOpponent(false)
      }
      
      console.log(`✅ [InteractiveP2P] Restored battle state - Turn ${data.currentTurn}, Time left: ${data.turnTimeLeft}s`)
    }
    
    const handleBattleAlreadyEnded = (data: any) => {
      console.log('🔄 [InteractiveP2P] battle_already_ended received:', data)
      
      if (data.battleId !== battleId) return
      
      // AGC: Battle already ended - show result
      setBattleEnded(true)
      setWinner(data.winner)
      setTurnTimeLeft(0)
      
      const isPlayerWinner = data.winner?.toLowerCase() === playerAddress.toLowerCase()
      onBattleEnd(isPlayerWinner, currentTurn, data.isDraw || false, null)
    }
    
    const handleBattleNotFound = (data: any) => {
      console.log('🔄 [InteractiveP2P] battle_not_found received:', data)
      
      if (data.battleId !== battleId) return
      
      // AGC: Don't navigate away if this is a new battle (might not be initialized yet)
      // AGC: Check component mount time - if mounted less than 10 seconds ago, it's likely a new battle
      const timeSinceMount = Date.now() - componentMountTimeRef.current
      if (timeSinceMount < 10000) {
        console.log('🔄 [InteractiveP2P] Ignoring battle_not_found - component just mounted (', Math.round(timeSinceMount / 1000), 's ago) - battle may still be initializing')
        return
      }
      
      // AGC: If battle_start was received less than 10 seconds ago, it's likely a new battle
      const timeSinceBattleStart = battleStartTimeRef.current 
        ? Date.now() - battleStartTimeRef.current 
        : Infinity
      
      if (timeSinceBattleStart < 10000) {
        console.log('🔄 [InteractiveP2P] Ignoring battle_not_found - new battle still initializing (', Math.round(timeSinceBattleStart / 1000), 's ago)')
        return
      }
      
      // AGC: Battle not found and it's not a new battle - user may have stale state
      // AGC: Don't navigate away - battle might still be processing or user can manually navigate
      console.warn('⚠️ Battle not found on server - may have expired or already ended')
      // AGC: The battle might still be processing, so we'll wait for battle_end event
    }
    
    socket.on('battle_state_sync', handleBattleStateSync)
    socket.on('battle_already_ended', handleBattleAlreadyEnded)
    socket.on('battle_not_found', handleBattleNotFound)
    
    return () => {
      socket.off('battle_state_sync', handleBattleStateSync)
      socket.off('battle_already_ended', handleBattleAlreadyEnded)
      socket.off('battle_not_found', handleBattleNotFound)
    }
  }, [socket, battleId, playerAddress, currentTurn, onBattleEnd, onBackToMenu])

  const handleSubmitTurn = () => {
    submitTurn(selectedChoice, selectedElement)
  }

  if (!socket) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-900 to-slate-900 p-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-slate-700 rounded-lg p-6 text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <h2 className="text-2xl font-bold text-white mb-2">Connecting to battle server...</h2>
            <p className="text-slate-300">
              Waiting for real-time socket connection. This can take a few seconds inside Farcaster mini apps.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <main className="pt-[72px] pb-[88px] px-3 min-h-screen bg-gradient-to-b from-slate-900 via-purple-900 to-slate-900">
      <div className="max-w-6xl mx-auto">
        <BattleTurnModal
          isOpen={!!turnSnapshot && !battleEnded}
          turn={turnSnapshot}
          onClose={() => setTurnSnapshot(null)}
          turnTimeLeft={turnTimeLeft}
          contextLabel="P2P Battle Turn"
        />

        {showProcessingOverlay && (
          <>
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 pointer-events-none"></div>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div
                role="status"
                aria-live="assertive"
                className="w-full max-w-md rounded-2xl border border-blue-400/70 bg-slate-900/95 text-center shadow-[0_20px_60px_rgba(0,0,0,0.65)] px-6 py-8"
              >
                <div className="mx-auto mb-4 h-14 w-14 animate-spin rounded-full border-4 border-blue-300 border-t-transparent"></div>
                <h3 className="text-2xl font-bold text-blue-100 mb-2">Processing Battle…</h3>
                <p className="text-slate-100/80 text-sm leading-relaxed">
                  Finalizing PvP settlement on Base. Please keep this screen open until the result modal finishes.
                </p>
              </div>
            </div>
          </>
        )}

        {/* AGC: BattleTop - Clean 3-column battle header */}
        <BattleTop
          playerAddress={playerAddress}
          playerStats={playerStats as BattleTopPlayerStats}
          playerEnergy={yourEnergy}
          playerBuffs={selectedBuffs}
          opponentInfo={{
            label: 'Opponent',
            level: opponentLevel,
            energy: opponentEnergy,
            address: opponentAddress
          } as OpponentInfo}
          opponentBuffs={[]}
          currentTurn={currentTurn}
          turnTimeLeft={turnTimeLeft}
          turnSubmitted={turnSubmitted}
          waitingForOpponent={waitingForOpponent}
          battleEnded={battleEnded}
          winner={winner}
          onBackToMenu={onBackToMenu}
        />

        {/* AGC: BattleCenter - Reusable battle center component */}
        <BattleCenter
          turnSubmitted={turnSubmitted}
          waitingForOpponent={waitingForOpponent}
          battleEnded={battleEnded}
          turnTimeLeft={turnTimeLeft}
          selectedChoice={selectedChoice}
          selectedElement={selectedElement}
          onChoiceSelect={(choice) => {
            setSelectedChoice(choice)
            const index = origamiOptions.indexOf(choice)
            if (index >= 0) {
              setChoiceCarouselIndex(index)
            }
            focusSubmitButton()
          }}
          onElementSelect={(element) => {
            setSelectedElement(element)
            const index = elementOptions.indexOf(element)
            if (index >= 0) {
              setElementCarouselIndex(index)
            }
            focusSubmitButton()
          }}
          onSubmitTurn={handleSubmitTurn}
          getChoiceImage={getChoiceImage}
          getElementEmoji={getElementEmoji}
          origamiOptions={origamiOptions}
          elementOptions={elementOptions}
          // AGC: Potion props
          equippedPotion={equippedPotion}
          selectedPotion={selectedPotionForTurn}
          onPotionSelect={(potionId) => {
            setSelectedPotionForTurn(potionId)
          }}
          potionUsed={potionUsed}
        />

        {/* AGC: BattleLog - Reusable battle log component */}
        <BattleLog
          battleLog={battleLog as BattleLogTurnResult[]}
          getChoiceImage={getChoiceImage}
          getElementEmoji={getElementEmoji}
          opponentLabel="Opponent"
        />
      </div>
    </main>
  )
}
